import { initCommerceApiWithGlobals } from 'common-api';
import { logger, useAdobeBasketDetails, isBrowser } from 'common-ui';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { getRecoil } from 'recoil-nexus';
import type { CartItemProps } from 'tsconfig/types';
import { authAtom } from '../../../atoms/authAtom';
import { cartAtom, commerceApiExtraConfig } from '../../../atoms/cartAtom';
import { notificationsAtom } from '../../../atoms/notificationsAtom';
import { cartNotification } from '../../../helpers/cartNotification';
import { commonHandlerForWebApiRequestError } from '../../../helpers/commonHandlerForWebApiRequestError';
import { getProducts } from '../../../helpers/getProducts';
import { getCurrentCartIdFromCookie } from '../../../helpers/utils';
import useWaitForCartToLoad from './useWaitForCartToLoad';
import { reloadAuthToken } from '../../../helpers/auth/reloadAuthToken';

export function useAddToCartWithWebApi(): (items: CartItemProps[]) => Promise<void> {
  const [, setCartData] = useRecoilState(cartAtom);
  const setNotification = useSetRecoilState(notificationsAtom);
  const waitForCartToLoad = useWaitForCartToLoad();
  const adobeBasketDetails = useAdobeBasketDetails();

  const prepareCartItems = (cartItems: CartItemProps[]) =>
    cartItems.map(({ itemCode, quantity, itemType, giftVoucherDetails, upsellOfferDetails }) => ({
      itemCode: String(itemCode),
      quantity: Number(quantity),
      ...(itemType && { itemType }),
      ...(giftVoucherDetails && { giftVoucherDetails }),
      ...(upsellOfferDetails
        ? {
            classificationDetailsInput: {
              upsell: { type: upsellOfferDetails.type.toUpperCase(), rule: upsellOfferDetails?.rule || '' },
            },
          }
        : {}),
    }));

  const updateCartData = async (newCartData: any) => {
    const productArray = await getProducts(newCartData);
    setCartData(prevState => ({
      ...prevState,
      cartIsLoaded: true,
      cartItems: {
        ...prevState.cartItems,
        ...newCartData,
        ...(productArray?.length ? { lineItems: productArray } : {}),
      },
    }));
  };

  const showNotification = (cartItems: CartItemProps[]) => {
    const minicartPayload =
      cartItems.length > 1
        ? cartItems.find(item => item.upsellOfferDetails?.type.toLowerCase() !== 'wineplan')
        : cartItems[0];

    if (!minicartPayload) {
      logger.log('No valid item found for notification');
      return;
    }

    const { itemCode, quantity } = minicartPayload;
    const notification = cartNotification(itemCode, quantity.toString());
    setNotification([notification]);
  };

  const handleAdobeBasketDetails = () => {
    const locationHref = isBrowser() ? window.location.href : '/';
    if (!locationHref.includes('/cart')) {
      adobeBasketDetails();
    }
  };

  const addItemsToCart = async (cartItems: CartItemProps[], authToken: string) => {
    const commerceApi = initCommerceApiWithGlobals(commerceApiExtraConfig(authToken));
    const { cartId, cartVersion } = getCurrentCartIdFromCookie();
    const items = prepareCartItems(cartItems);

    return commerceApi.CartPreferences.addItemToCart({
      cartId,
      data: { version: cartVersion, items },
    });
  };

  const doAddToCart = async (cartItems: CartItemProps[]) => {
    if (!isBrowser()) {
      logger.log('useAddToCartWithWebApi skipping as not in browser');
      return;
    }

    let authAtomState = getRecoil(authAtom);

    if (!authAtomState?.publicCsrf) {
      authAtomState = (await reloadAuthToken()) || authAtomState;
    }

    const newCartData = await addItemsToCart(cartItems, authAtomState.publicCsrf);

    await updateCartData(newCartData);

    showNotification(cartItems);

    handleAdobeBasketDetails();
  };

  return async (cartItems: CartItemProps[]) => {
    try {
      await waitForCartToLoad();
      await doAddToCart(cartItems);
    } catch (error: any) {
      logger.log('Error occurred while adding to cart:', error);
      return commonHandlerForWebApiRequestError(error, () => doAddToCart(cartItems));
    }
  };
}
